import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import * as S from './Table.styles';
import { columnsFormatter } from './TableFormatterUtils.inner';
import useTableSelectRow from './Hooks/useTableSelectRow.inner';

const paginationOptions = (
  totalElements,
  onPageChange,
  active,
  sizePerPage
) => ({
  pageStartIndex: 1,
  page: active,
  sizePerPage: sizePerPage,
  totalSize: totalElements, // Total data size. It's necessary when remote is enabled
  showTotal: true,
  firstPageText: '|<', // the text of first page button
  prePageText: '<', // the text of previous page button
  nextPageText: '>', // the text of next page button
  lastPageText: '>|', // the text of last page button
  withFirstAndLast: true, // hide the going to first and last page button
  alwaysShowAllBtns: true, // always show the next and previous page button
  hideSizePerPage: true, // hide the size per page dropdown
  hidePageListOnlyOnePage: true, // hide pagination bar when only one page, default is false
  disablePageTitle: true,
  onPageChange: onPageChange,
  paginationTotalRenderer: function pagination(from, to, size) {
    return (
      <span className='pagination__total'>
        {from} - {to} de {size}
      </span>
    );
  },
  pageListRenderer: pageListRenderer,
});

const isALongPaginationNumber = (page) => {
  return !Number.isNaN(Number(page)) && page.toString().length > 2;
};

const activeClass = (page) => {
  return isALongPaginationNumber(page.page)
    ? 'page--active-pill'
    : 'page--active';
};

const pageListRenderer = ({ pages, onPageChange }) => {
  return (
    <div className='pagination__list-of-buttons'>
      {pages.map((page) => (
        <button
          key={page.page}
          className={`pagination-list-of-buttons__button
                          ${page.active ? activeClass(page) : ''}
                          ${page.disabled ? ' disabled' : ''}`}
          onClick={() => onPageChange(page.page)}
        >
          {page.page}
        </button>
      ))}
    </div>
  );
};

const Table = ({
  dataListContent,
  columns,
  totalElements,
  onPageChange,
  pageActive,
  noDataIndication,
  remote = {},
  sizePerPage = 10,
  clickableRows = false,
  defaultSorted = [],
  headerClasses = 'headerClasses',
  rowEvents = {},
  wrapperClasses = '',
  selectRow = false,
  scroll,
  hasPagination = true,
}) => {
  const refTable = useRef(null);

  const columnsFormatted = columnsFormatter({
    columns: columns,
    clickableRows: clickableRows,
  });

  const currentIndex = useCallback((index) => {
    return parseInt(pageActive - 1) * sizePerPage + index;
  });

  const data = (dataListContent || []).map((item, index) => ({
    ...item,
    index: currentIndex(index),
  }));

  const [tableSelectRow, setPageSelectRow, setRefTable] = useTableSelectRow({
    selectRow,
    sizePerPage,
    pageActive,
    data,
  });

  useEffect(() => {
    if (selectRow && refTable?.current) setRefTable(refTable.current);
  }, [selectRow, setRefTable]);

  return (
    <S.Table clickableRows={clickableRows} ref={refTable} scroll={scroll}>
      <ToolkitProvider
        keyField='index'
        data={data}
        columns={columnsFormatted}
        search
      >
        {(props) => (
          <BootstrapTable
            pagination={
              hasPagination &&
              paginationFactory(
                paginationOptions(
                  totalElements,
                  (page, size) =>
                    onPageChange(page, size) & setPageSelectRow(page),
                  pageActive,
                  sizePerPage
                )
              )
            }
            remote={remote}
            onTableChange={(e) => e}
            bordered={false}
            defaultSorted={defaultSorted}
            {...props.baseProps}
            hover
            noDataIndication={noDataIndication()}
            headerClasses={headerClasses}
            rowEvents={rowEvents}
            wrapperClasses={wrapperClasses}
            selectRow={tableSelectRow}
          />
        )}
      </ToolkitProvider>
    </S.Table>
  );
};

Table.propTypes = {
  dataListContent: PropTypes.array,
  columns: PropTypes.array.isRequired,
  totalElements: PropTypes.number,
  onPageChange: PropTypes.func,
  pageActive: PropTypes.number.isRequired,
  noDataIndication: PropTypes.func.isRequired,
  remote: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pagination: PropTypes.bool,
      filter: PropTypes.bool,
      sort: PropTypes.bool,
      cellEdit: PropTypes.bool,
      search: PropTypes.bool,
    }),
  ]),
  sizePerPage: PropTypes.number,
  clickableRows: PropTypes.bool,
  defaultSorted: PropTypes.array,
  headerClasses: PropTypes.string,
  rowEvents: PropTypes.func,
  wrapperClasses: PropTypes.string,
  selectRow: PropTypes.object,
  scroll: PropTypes.bool,
  hasPagination: PropTypes.bool,
};

export default Table;
